import styled from "@emotion/styled";
import pathOr from "ramda/src/pathOr";
import { OptionType } from "@atlaskit/select";

import Avatar from "@mui/material/Avatar";
import { FC, MouseEvent, useMemo, useState } from "react";
import { COLOR_DODGER_BLUE, COLOR_PACIFIC_BLUE } from "../../constants/styles";
import { IPlacementWorksheetUnitOccupant } from "../../services/PlacementWorksheetApi";
import { ResidentMenu } from "../ResidentMenu";
import { usePlacementWorksheetSettingsContext } from "../../contexts/PlacementWorksheetContext";
import { normalize } from "../../helpers/normalize";
import { Typography } from "../../UI/Typography";

const AvatarWrapper = styled.span`
  display: inline-block;
  margin: 2px;

  .avatar--selected {
    box-sizing: border-box;
    border: 2px solid ${COLOR_DODGER_BLUE};
    box-shadow: inset 0px 0px 0px 1px #fff;
  }

  &:hover {
    transform: scale(1.1);
    transition: transform 0.2s ease;
  }
`;

interface ITableRoomsCellItemProps {
  unitId: number;
  resident: IPlacementWorksheetUnitOccupant;
}

export const TableRoomsCellItem: FC<ITableRoomsCellItemProps> = ({ resident, unitId }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const { labels, colors } = usePlacementWorksheetSettingsContext();

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = useMemo(() => !!anchorEl, [anchorEl]);

  const roomOptions: OptionType[] = useMemo(() => labels?.map(({ id, label }) => ({ value: id, label })), [
    normalize(labels),
  ]);

  const AVATAR_BG_COLOR = {
    new: colors.newResidentColor,
    renewing: colors.renewingResidentColor,
  };

  // Get resident initials
  const getInitials = () => {
    if (!resident) return "";
    return `${resident.firstName ? resident.firstName.charAt(0) : ''}${resident.lastName ? resident.lastName.charAt(0) : ''}`;
  };

  return (
    <>
      <AvatarWrapper role="button" onClick={handleClick}>
        <Avatar
          className={open ? "avatar--selected" : ""}
          variant="rounded"
          sx={{
            width: 28,
            height: 28,
            bgcolor: pathOr(COLOR_PACIFIC_BLUE, [resident?.status || "missing"], AVATAR_BG_COLOR),
          }}
        >
          <Typography variant="caption" fontWeight={700}>
            {getInitials()}
          </Typography>
        </Avatar>
      </AvatarWrapper>
      <ResidentMenu
        l4Id={resident?.l4Id}
        unitId={unitId}
        title={`${resident?.firstName || ''} ${resident?.lastName || ''}`}
        open={open}
        residentId={resident?.id}
        anchorEl={anchorEl}
        onClose={handleClose}
        roomOptions={roomOptions}
      />
    </>
  );
};
